import {
  postAnalyticsEvent,
  getParsePointersForSelectedTicket,
  getRecommendedMLTagsEventData,
  getCommerceEventData,
  getParsePointersForSelectedNonHistoricalTicket,
} from './common';
import moment from 'moment';

export const ML_EVENTS = {
  MACRO_RECOS_DISPLAYED: 'MACRO_RECOS_DISPLAYED',
  MACRO_RECO_VIEWED: 'MACRO_RECO_VIEWED',
  MACRO_RECO_COPIED: 'MACRO_RECO_COPIED',
  MACRO_RECOS_CLOSED: 'MACRO_RECOS_CLOSED',
  MACRO_RECOS_FETCHED: 'MACRO_RECOS_FETCHED',
  MULTI_INTENT_WARNING_DISPLAYED: 'MULTI_INTENT_WARNING_DISPLAYED',
  TAG_RECO_SELECTED: 'TAG_RECO_SELECTED',
  TAG_RECOS_FETCHED: 'TAG_RECOS_FETCHED',
  TAG_RECOS_DISPLAYED: 'TAG_RECOS_DISPLAYED',
  ESCALATION_RECO_DISPLAYED: 'ESCALATION_RECO_DISPLAYED',
  COMMERCE_RECOS_FETCHED: 'COMMERCE_RECOS_FETCHED',
  COMMERCE_RECOS_DISPLAYED: 'COMMERCE_RECOS_DISPLAYED',
  COMMERCE_RECO_VIEWED: 'COMMERCE_RECO_VIEWED',
  COMMERCE_RECO_COPIED: 'COMMERCE_RECO_COPIED',
  EASY_CONNECTION_FETCHED: 'EASY_CONNECTION_FETCHED',
  EASY_CONNECTION_INSERTED: 'EASY_CONNECTION_INSERTED',
  PREVIOUS_CONVERSATIONS_CLICKED: 'PREVIOUS_CONVERSATIONS_CLICKED',
};

const getMacroArticleId = (macro) => (macro ? macro.macro_article_id : 'unknown');

const getRecommendedMacrosMacroIds = (macrosArray) => {
  if (!macrosArray || !Array.isArray(macrosArray)) {
    return [];
  }

  return macrosArray.map((macro) => getMacroArticleId(macro));
};

/*
  Note for reviewer:
  getParsePointersForSelectedTicket returns these:
    - conversationPtr
    - simplrExpertPtr
    - simplrAccountPtr
*/

// Begin Chat Macro Recommendation feature events:

// MACRO_RECO_VIEWED
export const trackChatMacrosViewed = async (macro) => {
  const parsePointers = getParsePointersForSelectedTicket();
  const macroArticleId = getMacroArticleId(macro);
  const params = { macroArticleId };

  return await postAnalyticsEvent(ML_EVENTS.MACRO_RECO_VIEWED, parsePointers, params);
};

// MACRO_RECO_COPIED
export const trackChatMacrosCopied = async (macro, source) => {
  const parsePointers = getParsePointersForSelectedTicket();
  const macroArticleId = getMacroArticleId(macro);
  const params = { macroArticleId, source };

  return await postAnalyticsEvent(ML_EVENTS.MACRO_RECO_COPIED, parsePointers, params);
};

// MACRO_RECOS_CLOSED
export const trackChatMacrosClosed = async (source) => {
  const parsePointers = getParsePointersForSelectedTicket();
  const params = { source };

  return await postAnalyticsEvent(ML_EVENTS.MACRO_RECOS_CLOSED, parsePointers, params);
};

// MACRO_RECOS_FETCHED
export const trackChatMacrosFetched = async (macrosData) => {
  const { macros_recommended, toDisplay, requestDetails } = macrosData;
  const parsePointers = getParsePointersForSelectedTicket();
  const macroIds = getRecommendedMacrosMacroIds(macros_recommended);
  const params = { macroIds, toDisplay, requestDetails };

  return await postAnalyticsEvent(ML_EVENTS.MACRO_RECOS_FETCHED, parsePointers, params);
};

// MACRO_RECOS_DISPLAYED
export const trackChatMacrosDisplayed = async (macrosData) => {
  const { macros_recommended, requestDetails } = macrosData;
  const parsePointers = getParsePointersForSelectedTicket();
  const macroIds = getRecommendedMacrosMacroIds(macros_recommended);
  const params = { macroIds, requestDetails };

  return await postAnalyticsEvent(ML_EVENTS.MACRO_RECOS_DISPLAYED, parsePointers, params);
};

// Begin Multi-Intent Warning feature events:

export const trackMultiIntentWarningDisplayed = async () => {
  const parsePointers = getParsePointersForSelectedTicket();
  const params = {};

  return await postAnalyticsEvent(ML_EVENTS.MULTI_INTENT_WARNING_DISPLAYED, parsePointers, params);
};

// TAG_RECO_SELECTED
export const tagRecoSelected = async (selectedTag) => {
  const parsePointers = getParsePointersForSelectedTicket();
  const params = {
    createdAt: moment().format(),
    selectedTag: selectedTag,
  };

  return await postAnalyticsEvent(ML_EVENTS.TAG_RECO_SELECTED, parsePointers, params);
};

// TAG_RECOS_DISPLAYED
export const tagRecosDisplayed = async () => {
  const parsePointers = getParsePointersForSelectedTicket();
  const { tags, requestDetails } = getRecommendedMLTagsEventData();

  const params = {
    createdAt: moment().format(),
    recommendedMlTags: tags,
    requestDetails: requestDetails,
  };

  return await postAnalyticsEvent(ML_EVENTS.TAG_RECOS_DISPLAYED, parsePointers, params);
};

/* ESCALATION_RECO_DISPLAYED
    - eventName
    - createdAt
    - conversationId (pointer)
    - expertId (pointer)
    - simplrAccount (pointer)
    - businessAlertPtr: alert id
*/
export const escalationRecosDisplayed = async (businessAlertPtr) => {
  const parsePointers = getParsePointersForSelectedTicket();
  const eventName = ML_EVENTS.ESCALATION_RECO_DISPLAYED;

  const params = {
    createdAt: moment().format(),
    businessAlertPtr,
  };

  return await postAnalyticsEvent(eventName, parsePointers, params);
};

export const commerceRecosFetched = async (data) => {
  const { toDisplay, requestDetails, detectedProductId } = data;
  const parsePointers = getParsePointersForSelectedTicket();

  const params = { toDisplay, requestDetails, detectedProductId };

  return await postAnalyticsEvent(ML_EVENTS.COMMERCE_RECOS_FETCHED, parsePointers, params);
};

export const easyConnectionFetched = async (data) => {
  const { toDisplay, customerName, requestDetails, connections } = data;
  const parsePointers = getParsePointersForSelectedTicket();
  const params = { toDisplay, customerName, requestDetails, connections };

  return await postAnalyticsEvent(ML_EVENTS.EASY_CONNECTION_FETCHED, parsePointers, params);
};

export const easyConnectionInserted = async (data) => {
  const { originalText, newText, options } = data;
  const parsePointers = getParsePointersForSelectedTicket();
  const params = { originalText, newText, options };

  return await postAnalyticsEvent(ML_EVENTS.EASY_CONNECTION_INSERTED, parsePointers, params);
};

export const commerceRecosDisplayed = async () => {
  const { requestDetails, detectedProductId } = getCommerceEventData();
  const parsePointers = getParsePointersForSelectedTicket();

  const params = { requestDetails, detectedProductId };

  return await postAnalyticsEvent(ML_EVENTS.COMMERCE_RECOS_DISPLAYED, parsePointers, params);
};

export const commerceRecoViewed = async () => {
  const { requestDetails, detectedProductId, selectedProductRecommendationId } = getCommerceEventData();
  const parsePointers = getParsePointersForSelectedTicket();

  const params = {
    requestDetails,
    detectedProductId,
    selectedProductRecommendationId,
  };

  return await postAnalyticsEvent(ML_EVENTS.COMMERCE_RECO_VIEWED, parsePointers, params);
};

export const commerceRecoCopied = async (text) => {
  const { requestDetails, productDetectedId, selectedRecommendationId } = getCommerceEventData();
  const parsePointers = getParsePointersForSelectedTicket();

  const params = {
    requestDetails,
    text,
    detectedProductId: productDetectedId,
    selectedProductRecommendationId: selectedRecommendationId,
  };

  return await postAnalyticsEvent(ML_EVENTS.COMMERCE_RECO_COPIED, parsePointers, params);
};

export const previousConversationsClicked = async ({ conversationId }) => {
  const customParsePointers = conversationId
    ? [{ name: 'previousConversationPtr', className: 'Conversation', id: conversationId }]
    : [];
  const parsePointers = getParsePointersForSelectedNonHistoricalTicket();

  return await postAnalyticsEvent(ML_EVENTS.PREVIOUS_CONVERSATIONS_CLICKED, parsePointers, {}, customParsePointers);
};
